import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatchAsync } from '@src/utils'
import { SynthesizeSpeechMutation } from '../graphql/synthesizeSpeech.generated'
import { deleteSpeechChunk, writeSpeechChunk } from '../services/noraSpeechFiles'

export type SpeechChunk = { id: string; text: string }

export enum SpeechStatus {
  Queued = 'queued',
  Synthesizing = 'synthesizing',
  Ready = 'ready',
  Playing = 'playing',
}

type SpeechRecord = {
  chunk: SpeechChunk
  filePath?: string
  queuedAtMs: number
  readyAtMs?: number
  playbackStartedAtMs?: number
  status: SpeechStatus
}

export type NoraVoiceState = {
  currentAudioPath: string | null
  onPlaybackEnd: () => void
  onPlaybackError: (error: Error) => void
}

const MAX_SYNTHESIS_IN_FLIGHT = 2

const now = () => Date.now()

export const useNoraVoice = () => {
  const dispatchAsync = useDispatchAsync()

  const recordsRef = useRef<SpeechRecord[]>([])
  const synthesisInFlightRef = useRef(0)
  const generationRef = useRef(0)
  const cachedFilePathsRef = useRef(new Set<string>())
  const currentAudioPathRef = useRef<string | null>(null)
  const spokenChunkIdsRef = useRef(new Set<string>())
  const readyPlaybackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [currentAudioPath, setCurrentAudioPath] = useState<string | null>(null)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const setCurrentPlaybackFile = useCallback((filePath: string | null) => {
    currentAudioPathRef.current = filePath
    setCurrentAudioPath(filePath)
  }, [])

  const cleanupFile = useCallback(async (filePath: string) => {
    cachedFilePathsRef.current.delete(filePath)
    await deleteSpeechChunk(filePath)
  }, [])

  const clearCachedFiles = useCallback(async () => {
    const filePaths = Array.from(cachedFilePathsRef.current)
    cachedFilePathsRef.current.clear()
    await Promise.all(filePaths.map(deleteSpeechChunk))
  }, [])

  const startReadyPlayback = useCallback(() => {
    if (currentAudioPathRef.current) {
      return
    }

    const next = recordsRef.current.at(0)
    if (next?.status === SpeechStatus.Ready && next.filePath) {
      next.status = SpeechStatus.Playing
      next.playbackStartedAtMs = now()
      setCurrentPlaybackFile(next.filePath)
      setIsSpeaking(true)
    } else if (!next && synthesisInFlightRef.current === 0) {
      setIsSpeaking(false)
    }
  }, [setCurrentPlaybackFile])

  const scheduleSynthesisRef = useRef<(generation?: number) => void>(() => undefined)

  const synthesizeRecord = useCallback(
    async (record: SpeechRecord, generation: number) => {
      const synthesisRecord = record
      try {
        const speech = await dispatchAsync<
          SynthesizeSpeechMutation['synthesizeSpeech'] | undefined
        >({
          type: 'nora/synthesizeSpeech',
          payload: { chunkId: synthesisRecord.chunk.id, text: synthesisRecord.chunk.text },
        })
        if (generationRef.current !== generation || !speech) {
          return
        }
        if (speech.chunkId !== synthesisRecord.chunk.id) {
          throw new Error('Nora speech chunk mismatch')
        }

        const filePath = await writeSpeechChunk(speech)
        if (generationRef.current !== generation) {
          await deleteSpeechChunk(filePath)
          return
        }

        cachedFilePathsRef.current.add(filePath)
        synthesisRecord.filePath = filePath
        synthesisRecord.readyAtMs = now()
        synthesisRecord.status = SpeechStatus.Ready
        startReadyPlayback()
      } catch {
        if (synthesisRecord.filePath) {
          await cleanupFile(synthesisRecord.filePath)
        }
        if (generationRef.current === generation) {
          synthesisRecord.status = SpeechStatus.Ready
          setError(new Error('Nora speech failed'))
          recordsRef.current = recordsRef.current.filter(
            (candidate) => candidate !== synthesisRecord,
          )
          startReadyPlayback()
        }
      } finally {
        synthesisInFlightRef.current -= 1
        scheduleSynthesisRef.current()
        if (generationRef.current === generation) {
          startReadyPlayback()
        }
      }
    },
    [cleanupFile, dispatchAsync, startReadyPlayback],
  )

  const scheduleSynthesis = useCallback(
    (generation = generationRef.current) => {
      while (synthesisInFlightRef.current < MAX_SYNTHESIS_IN_FLIGHT) {
        const next = recordsRef.current.find((record) => record.status === SpeechStatus.Queued)
        if (!next) {
          return
        }

        next.status = SpeechStatus.Synthesizing
        synthesisInFlightRef.current += 1
        void synthesizeRecord(next, generation)
      }
    },
    [synthesizeRecord],
  )
  scheduleSynthesisRef.current = scheduleSynthesis

  const onPlaybackEnd = useCallback(() => {
    const completed = recordsRef.current.shift()
    if (completed?.filePath) {
      void cleanupFile(completed.filePath)
    }

    setCurrentPlaybackFile(null)
    if (readyPlaybackTimerRef.current) {
      clearTimeout(readyPlaybackTimerRef.current)
    }
    readyPlaybackTimerRef.current = setTimeout(() => {
      readyPlaybackTimerRef.current = null
      startReadyPlayback()
    }, 0)
  }, [cleanupFile, setCurrentPlaybackFile, startReadyPlayback])

  const stop = useCallback(async () => {
    generationRef.current += 1
    if (readyPlaybackTimerRef.current) {
      clearTimeout(readyPlaybackTimerRef.current)
      readyPlaybackTimerRef.current = null
    }
    recordsRef.current = []
    spokenChunkIdsRef.current.clear()
    setCurrentPlaybackFile(null)
    setIsSpeaking(false)
    await clearCachedFiles()
  }, [clearCachedFiles, setCurrentPlaybackFile])

  const speak = useCallback(
    (chunks: SpeechChunk[]) => {
      const uniqueChunks = chunks.filter((chunk) => {
        if (spokenChunkIdsRef.current.has(chunk.id)) {
          return false
        }
        spokenChunkIdsRef.current.add(chunk.id)
        return true
      })
      if (uniqueChunks.length === 0) {
        return
      }

      setError(null)
      setIsSpeaking(true)
      recordsRef.current.push(
        ...uniqueChunks.map((chunk) => ({
          chunk,
          queuedAtMs: now(),
          status: SpeechStatus.Queued,
        })),
      )
      scheduleSynthesis()
    },
    [scheduleSynthesis],
  )

  const onPlaybackError = useCallback(
    (playbackError: Error) => {
      setError(playbackError)
      onPlaybackEnd()
    },
    [onPlaybackEnd],
  )

  useEffect(
    () => () => {
      void stop()
    },
    [stop],
  )

  return { speak, stop, isSpeaking, error, currentAudioPath, onPlaybackEnd, onPlaybackError }
}
